import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Play,
  Pause,
  StopCircle,
  Trash2,
  BarChart3,
  Trophy,
  Users,
  MousePointerClick,
  Clock,
  CheckCircle2,
  AlertCircle,
  Link,
  Copy,
  ExternalLink,
  Pencil,
  Eye
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ABTest } from '../../services/abTestService';
import Badge from '../ui/Badge';
import Button from '../ui/Button';

interface ABTestCardProps {
  test: ABTest;
  onStart?: (testId: string) => void;
  onPause?: (testId: string) => void;
  onEnd?: (testId: string) => void;
  onDelete?: (testId: string) => void;
  onViewDetails?: (testId: string) => void;
  onEdit?: (test: ABTest) => void;
}

const ABTestCard: React.FC<ABTestCardProps> = ({
  test,
  onStart,
  onPause,
  onEnd,
  onDelete,
  onViewDetails,
  onEdit
}) => {
  const { t } = useTranslation();

  const getStatusConfig = (status: string) => {
    const configs: Record<string, { variant: 'gray' | 'success' | 'warning' | 'primary'; label: string; icon: React.ReactNode }> = {
      draft: { variant: 'gray', label: t('abTesting.status.draft', 'Rascunho'), icon: <Clock className="w-3 h-3" /> },
      running: { variant: 'success', label: t('abTesting.status.running', 'Em andamento'), icon: <Play className="w-3 h-3" /> },
      paused: { variant: 'warning', label: t('abTesting.status.paused', 'Pausado'), icon: <Pause className="w-3 h-3" /> },
      completed: { variant: 'primary', label: t('abTesting.status.completed', 'Concluido'), icon: <CheckCircle2 className="w-3 h-3" /> }
    };
    return configs[status] || configs.draft;
  };

  const statusConfig = getStatusConfig(test.status);
  const stats = test.stats;
  const controlVariant = stats?.variants.find(v => v.isControl);
  const testVariant = stats?.variants.find(v => !v.isControl);

  const getRateColor = (rate: number) => {
    if (rate >= 5) return 'text-green-600 dark:text-green-400';
    if (rate >= 2) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-gray-600 dark:text-gray-400';
  };

  const formatDuration = (startedAt?: string) => {
    if (!startedAt) return '-';
    const start = new Date(startedAt);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Hoje';
    if (days === 1) return '1 dia';
    return `${days} dias`;
  };

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const baseUrl = apiUrl.replace('/api', '');
  const testUrl = `${baseUrl}/api/landing-pages/ab/t/${test.slug}`;

  const copyUrl = () => {
    navigator.clipboard.writeText(testUrl);
    toast.success(t('common.copied', 'Copied!'));
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 hover:shadow-lg transition-all">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">{test.name}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {test.landingPage?.name}
          </p>
        </div>
        <Badge variant={statusConfig.variant}>
          <span className="flex items-center gap-1">
            {statusConfig.icon}
            {statusConfig.label}
          </span>
        </Badge>
      </div>

      {/* Test URL */}
      {test.slug && (
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Link className="w-4 h-4 text-gray-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              {t('abTesting.testUrl', 'URL do Teste')}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs text-indigo-600 dark:text-indigo-400 truncate">
              {testUrl}
            </code>
            <button
              onClick={copyUrl}
              className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
              title={t('common.copy', 'Copiar')}
            >
              <Copy className="w-4 h-4 text-gray-500" />
            </button>
            {test.status === 'running' && (
              <a
                href={testUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
                title={t('common.open', 'Abrir')}
              >
                <ExternalLink className="w-4 h-4 text-gray-500" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Page Previews */}
      {test.variants && test.variants.length >= 2 && (
        <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Eye className="w-4 h-4 text-gray-500" />
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              {t('abTesting.pagePreview', 'Paginas do Teste')}
            </span>
          </div>
          <div className="space-y-2">
            {test.variants.map((variant) => {
              const hasPublishedUrl = !!variant.landingPage?.published_url;
              const pageName = variant.landingPage?.name || `Pagina ${variant.landing_page_id?.slice(0, 8)}`;
              const pageUrl = variant.landingPage?.published_url;

              return (
                <a
                  key={variant.id}
                  href={hasPublishedUrl ? pageUrl : `/landing-pages/${variant.landing_page_id}/edit`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    variant.is_control
                      ? 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      : 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 hover:border-indigo-300'
                  }`}
                >
                  <ExternalLink className={`w-3.5 h-3.5 flex-shrink-0 ${variant.is_control ? 'text-gray-500' : 'text-indigo-500'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium truncate ${variant.is_control ? 'text-gray-700 dark:text-gray-300' : 'text-indigo-700 dark:text-indigo-300'}`}>
                        {pageName}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        variant.is_control
                          ? 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          : 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400'
                      }`}>
                        {variant.is_control ? 'Controle' : 'Variante'}
                      </span>
                    </div>
                    {hasPublishedUrl && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {pageUrl}
                      </p>
                    )}
                    {!hasPublishedUrl && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                        Nao publicada - clique para editar
                      </p>
                    )}
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Variants Comparison */}
      {stats && stats.variants.length >= 2 && (
        <div className="grid grid-cols-2 gap-4 mb-4">
          {/* Control */}
          <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                {t('abTesting.control', 'Controle')}
              </span>
              {test.winner_variant_id === controlVariant?.id && (
                <Trophy className="w-4 h-4 text-yellow-500" />
              )}
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  {t('abTesting.views', 'Visitas')}
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {controlVariant?.views.toLocaleString('pt-BR') || 0}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <MousePointerClick className="w-3.5 h-3.5" />
                  {t('abTesting.conversions', 'Conversoes')}
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {controlVariant?.conversions.toLocaleString('pt-BR') || 0}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm pt-1 border-t border-gray-200 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400">
                  {t('abTesting.conversionRate', 'Taxa')}
                </span>
                <span className={`font-bold ${getRateColor(controlVariant?.conversionRate || 0)}`}>
                  {controlVariant?.conversionRate.toFixed(2) || '0.00'}%
                </span>
              </div>
            </div>
          </div>

          {/* Variant */}
          <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-100 dark:border-indigo-800">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400 uppercase">
                {testVariant?.name || t('abTesting.variant', 'Variante')}
              </span>
              {test.winner_variant_id === testVariant?.id && (
                <Trophy className="w-4 h-4 text-yellow-500" />
              )}
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  {t('abTesting.views', 'Visitas')}
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {testVariant?.views.toLocaleString('pt-BR') || 0}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                  <MousePointerClick className="w-3.5 h-3.5" />
                  {t('abTesting.conversions', 'Conversoes')}
                </span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {testVariant?.conversions.toLocaleString('pt-BR') || 0}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm pt-1 border-t border-indigo-100 dark:border-indigo-800">
                <span className="text-gray-600 dark:text-gray-400">
                  {t('abTesting.conversionRate', 'Taxa')}
                </span>
                <span className={`font-bold ${getRateColor(testVariant?.conversionRate || 0)}`}>
                  {testVariant?.conversionRate.toFixed(2) || '0.00'}%
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Summary */}
      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4">
        {test.started_at && (
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {formatDuration(test.started_at)}
          </span>
        )}
        {test.confidence_level && test.confidence_level > 0 && (
          <span className="flex items-center gap-1">
            <BarChart3 className="w-4 h-4" />
            {t('abTesting.confidence', 'Confianca')}: {test.confidence_level.toFixed(0)}%
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        {test.status === 'draft' && onStart && (
          <Button size="sm" onClick={() => onStart(test.id)} leftIcon={<Play className="w-4 h-4" />}>
            {t('abTesting.startTest', 'Iniciar')}
          </Button>
        )}
        {test.status === 'running' && (
          <>
            {onPause && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onPause(test.id)}
                leftIcon={<Pause className="w-4 h-4" />}
              >
                {t('abTesting.pauseTest', 'Pausar')}
              </Button>
            )}
            {onEnd && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => onEnd(test.id)}
                leftIcon={<StopCircle className="w-4 h-4" />}
              >
                {t('abTesting.endTest', 'Encerrar')}
              </Button>
            )}
          </>
        )}
        {test.status === 'paused' && onStart && (
          <Button size="sm" onClick={() => onStart(test.id)} leftIcon={<Play className="w-4 h-4" />}>
            {t('abTesting.resumeTest', 'Retomar')}
          </Button>
        )}
        {onViewDetails && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onViewDetails(test.id)}
            leftIcon={<BarChart3 className="w-4 h-4" />}
          >
            {t('abTesting.viewDetails', 'Detalhes')}
          </Button>
        )}
        {onEdit && test.status !== 'completed' && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onEdit(test)}
            leftIcon={<Pencil className="w-4 h-4" />}
          >
            {t('common.edit', 'Editar')}
          </Button>
        )}
        {onDelete && test.status !== 'running' && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onDelete(test.id)}
            className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ABTestCard;
